import { useQuery } from '@tanstack/react-query';
import { getJson, sendJson } from '@/lib/api';
import { PERMISSION_MANAGE_ROLES, hasPermission, readMyIdentity, type MyIdentity } from '@/lib/events';

/** A role as the API sends it. */
export interface Role {
  readonly id: string;
  readonly key: string;
  readonly name: string;
  readonly description: string | null;
  readonly permissions: readonly string[];
  /** Seeded by the system: renamable, never archived. */
  readonly system: boolean;
  /** Sent back on every edit; a stale one is refused so two admins never overwrite each other. */
  readonly version: number;
  readonly memberCount: number;
}

/** Someone holding a role. */
export interface RoleMember {
  readonly id: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email: string;
}

/** One role and the people who hold it. */
export interface RoleDetail extends Role {
  readonly members: readonly RoleMember[];
}

/** Query keys, named once. */
export const ROLE_QUERY_KEY = {
  list: ['roles'],
  detail: (id: string) => ['roles', id],
  forUser: (userId: string) => ['user-roles', userId],
} as const;

/** The query key every caller of `/users/me` shares. */
export const MY_IDENTITY_QUERY_KEY = ['my-identity'] as const;

/** Every active role with its member count. */
export async function listRoles(signal?: AbortSignal): Promise<readonly Role[]> {
  return getJson<Role[]>('/roles', undefined, signal);
}

/** One role and its members. */
export async function readRole(id: string, signal?: AbortSignal): Promise<RoleDetail> {
  return getJson<RoleDetail>(`/roles/${id}`, undefined, signal);
}

/** Creates a role. */
export async function createRole(body: { name: string; description?: string; permissions: readonly string[] }): Promise<Role> {
  return sendJson<Role>('post', '/roles', body);
}

/** Edits a role; `version` is the one this screen read. */
export async function updateRole(
  id: string,
  body: { version: number; name?: string; description?: string; permissions?: readonly string[] },
): Promise<Role> {
  return sendJson<Role>('patch', `/roles/${id}`, body);
}

/** Archives a role: it stops granting anything to the people who hold it. */
export async function archiveRole(id: string): Promise<void> {
  await sendJson<unknown>('delete', `/roles/${id}`);
}

/** The roles one person holds. */
export async function listUserRoles(userId: string, signal?: AbortSignal): Promise<readonly Role[]> {
  return getJson<Role[]>(`/users/${userId}/roles`, undefined, signal);
}

/** Gives a person a role. */
export async function assignRole(userId: string, roleId: string): Promise<void> {
  await sendJson<unknown>('put', `/users/${userId}/roles/${roleId}`);
}

/** Takes a role away from a person. */
export async function unassignRole(userId: string, roleId: string): Promise<void> {
  await sendJson<unknown>('delete', `/users/${userId}/roles/${roleId}`);
}

/** The signed-in person, shared by every screen that asks. */
export function useMyIdentity() {
  return useQuery({
    queryKey: MY_IDENTITY_QUERY_KEY,
    queryFn: ({ signal }) => readMyIdentity(signal),
    staleTime: 5 * 60_000,
  });
}

/**
 * Whether this person may use the Roles screens: the rollout switch is on and
 * they hold manage_roles (superadmins hold it implicitly). The API enforces
 * the same; this only keeps dead links out of sight.
 *
 * @param identity - The signed-in person.
 * @returns True when the Roles screens should show.
 */
export function canManageRoles(identity: MyIdentity | undefined): boolean {
  return identity?.rbacEnabled === true && hasPermission(identity, PERMISSION_MANAGE_ROLES);
}

/** Stored tiers by the account type the API maps them to (mirrors `accountTypeForRole`). */
const ACCOUNT_TYPE_BY_TIER: Readonly<Record<string, string>> = {
  internal: 'staff',
  admin: 'staff',
  superadmin: 'staff',
  contractor: 'contractor',
};

/**
 * The account type a person's stored tier belongs to; anything unknown is a
 * client, as on the API.
 *
 * @param tier - The stored `users.role`.
 * @returns `staff`, `contractor` or `client`.
 */
export function accountTypeForTier(tier: string | undefined): string {
  return (tier === undefined ? undefined : ACCOUNT_TYPE_BY_TIER[tier]) ?? 'client';
}

/** A member's display name, falling back to their email. */
export function memberName(member: RoleMember): string {
  return [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email;
}
